"use client"

import React from 'react'
import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { Baby, Heart, Flower2, Leaf, Moon, AudioWaveform, ArrowRight } from "lucide-react"
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'

type AreaOfFocus = {
  icon: React.ComponentType<{ className?: string }>
  title: string
  description?: string
  bookingId?: string // Identifier for tracking which area was clicked
  slug?: string // Optional slug for routing, defaults to titleToSlug(title)
}

type AreasOfFocusProps = {
  areas?: AreaOfFocus[]
  title?: string
  subtitle?: string
  endtitle?: string
  onBook?: (areaId: string, areaTitle: string) => void
  fromHomepage?: boolean // Indicates if this component is used on the homepage
  fromAppointmentsPage?: boolean // Indicates if this component is used on the appointments page
  sectionId?: string // Optional section ID for anchor linking
}

// Helper function to convert title to URL-friendly slug
const titleToSlug = (title: string): string => {
  return title
    .toLowerCase()
    .replace(/&/g, 'and')
    .replace(/[^\w\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .trim()
}

const defaultAreas: AreaOfFocus[] = [
  { icon: Flower2, title: 'Fertility & Preconception', description: 'Support for people trying to conceive – with or without IVF – paying attention to cycles, stress, sleep and overall health alongside your medical care.', bookingId: 'fertility_preconception', slug: 'fertility-preconception' },
  { icon: Baby, title: 'Pregnancy & Postpartum', description: 'Care through all trimesters and into the fourth trimester, with support for comfort, sleep, anxiety, birth preparation and postnatal recovery.', bookingId: 'pregnancy_postpartum', slug: 'pregnancy-postpartum' },
  { icon: Heart, title: 'Menstrual & Hormonal Health', description: 'Support for painful, heavy or irregular periods, PMS/PMDD, perimenopause symptoms and cycle-related mood or sleep changes.', bookingId: 'menstrual_hormonal', slug: 'menstrual-hormonal-health' },
  { icon: Moon, title: 'Stress, Sleep & Digestive Balance', description: 'Care for the nervous system and digestion, including stress, burnout, sleep difficulties and IBS-type symptoms that sit under everything else.', bookingId: 'stress_sleep_digestive', slug: 'stress-sleep-digestion' },
  { icon: Leaf, title: 'Ongoing Women\'s Health & Long-Term Care', description: 'Many people stay for years, moving from cycles into fertility, pregnancy, postpartum and perimenopause with the same practitioner alongside them.', bookingId: 'ongoing_womens_health', slug: 'womens-health-long-term-care' },
  { icon: AudioWaveform, title: 'Other Concerns', description: 'While some concerns don\'t fit neatly into categories, there is also space for more complex presentations.', bookingId: 'other', slug: 'other-concerns' },
]

export function AreasOfFocus({ 
  areas = defaultAreas, 
  title = 'Areas of Focus',
  subtitle = 'These are the main areas of care Renee focuses on, supporting you through different stages of life with the same steady, relationship-based care.',
  endtitle = 'If you\'re booking for the first time, please choose an Initial Consultation & Treatment.',
  onBook,
  fromHomepage = false,
  fromAppointmentsPage = false,
  sectionId
}: AreasOfFocusProps) {
  return (
    <section id={sectionId} className="py-20 bg-gray-50">
      <div className="container mx-auto px-4 sm:w-2/3 md:w-full mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-12"
        >
          <h2 className="text-2xl md:text-3xl mb-4 text-[#3f4141] max-w-[900px] mx-auto">
            {title}
          </h2>
          {subtitle && (
            <p className="text-gray-600 max-w-2xl mx-auto">
              {subtitle}
            </p>
          )}
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8 max-w-5xl mx-auto place-items-center md:place-items-stretch">
          {areas.map((area, index) => (
            <motion.div
              key={area.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: index * 0.1 }}
              className="w-full"
            >
              <Card className="h-full border border-gray-200 shadow-sm flex flex-col">
                <CardHeader>
                  <area.icon className="h-8 w-8 text-[#DBB981] mb-4" />
                  <CardTitle className="text-[#3f4141] font-light text-lg md:text-xl">
                    {area.title}
                  </CardTitle>
                </CardHeader>
                {area.description && (
                  <CardContent className="flex-1 flex flex-col">
                    <CardDescription className="mb-3">{area.description}</CardDescription>
                    {area.title === 'Other Concerns' && (
                      <ul className="list-disc list-inside space-y-2 text-sm text-gray-600 mb-3">
                        <li>Tension-related pain</li>
                        <li>Chronic headaches or migraines</li>
                        <li>Complex symptom patterns</li>
                        <li>Cancer care and support</li>
                      </ul>
                    )}
                    <div className="mt-auto pt-4">
                      <Link 
                        to={`/what-we-help-with/${area.slug || titleToSlug(area.title)}`}
                        state={
                          fromHomepage 
                            ? { fromHomepage: true } 
                            : fromAppointmentsPage 
                            ? { fromAppointmentsPage: true } 
                            : undefined
                        }
                        className="text-[#c5a574] hover:text-[#DBB981] font-medium inline-flex items-center gap-1 transition-colors text-sm"
                      >
                        Learn more
                        <ArrowRight className="h-4 w-4" />
                      </Link>
                    </div>
                  </CardContent>
                )}
              </Card>
            </motion.div>
          ))}
        </div>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mt-12"
        >
          {endtitle && (
            <p className="text-gray-600 max-w-2xl mx-auto">
              {endtitle}
            </p>
          )}
        </motion.div>
      </div>
    </section>
  )
}

